import { Util } from "../../../../../utils/util";
import { CustomizeItemPreModifierMultiItem } from './customize-item-premodifier-bar';
import { HalfModifierSelection } from "../../../../../types/enums";
import { ModifierRenderHalfState } from "../../core/customize-item-core.interface";
import { CustomizeItemModifierBase } from "../base/customize-item-modifier-base";
import { ICustomizeItemModifierBaseElement } from "../base/customize-item-modifier-base.interface";
import { ICustomizeItemPremodifierBaseParams } from "../base/customize-item-premodifier-bar-base";

export interface ICustomizeItemModifierMultiElement extends ICustomizeItemModifierBaseElement {
    selectedData: Map<HalfModifierSelection, ModifierRenderHalfState>
}

/**
 * Represents a modifier option within the customize item UI, including any optional helper bars
 * like premodifiers or halfable selections.
 */
export class CustomizeItemModifierMultiItem extends CustomizeItemModifierBase<ICustomizeItemModifierMultiElement> {
    /**
     * Creates a modifier option element for the UI.
     * 
     * @param {ICustomizeItemModifierMultiElement} params - Configuration parameters for the modifier option.
     * @returns {HTMLElement} The constructed modifier option element.
     */
    protected _createModifierOption(params: ICustomizeItemModifierMultiElement): HTMLElement {
        const isHalfable = params.isHalfMode && (params.modifierDisplayGroup?.IsHalfable ?? false);

        const priceCaloriesId = params.modifierDisplayGroup === null
            ? `spn_customize_item_modifier_size_price_cal_${params.modifierId}`
            : `spn_customize_item_modifier_price_cal_${params.modifierDisplayGroup.ModifierDisplayGroupId}_${params.modifierId}`;

        let image = "";
        if (params.groupHasImage) {
            image = `
                <div class="customize-item-modifier-label-image">
                    ${(params.imageUrl !== "" && params.imageUrl !== null) ? `<img src="${params.imageUrl}" alt="" aria-hidden="true">` : ""}
                </div>`;
        }

        const html = `
            <div id="lbl_${params.id}" class="customize-item-modifier-multi" role="group" aria-labelledby="${params.id}_name">
                <div class="customize-item-modifier-label">
                    ${image}
                    <div class="customize-item-modifier-label-info">
                        <span id="${params.id}_name">${params.name}</span>
                        <span id="${priceCaloriesId}" class="price"></span>
                    </div>
                </div>

                <div id="${params.id}_checkboxes" class="customize-item-modifier-checkboxes" style="display: flex; gap: 3rem;"></div>
            </div>`;

        const div = Util.createHtmlElementFromTemplate(html);

        const checkboxesDiv = div.querySelector(`#${params.id}_checkboxes`)!;

        const leftId = isHalfable ? `${params.id}_${HalfModifierSelection.LEFT}` : params.id;
        const leftCheckbox = this._createCheckbox(leftId, params, HalfModifierSelection.LEFT);
        checkboxesDiv.appendChild(leftCheckbox);

        if (isHalfable) {
            const rightCheckbox = this._createCheckbox(`${params.id}_${HalfModifierSelection.RIGHT}`, params, HalfModifierSelection.RIGHT);
            checkboxesDiv.appendChild(rightCheckbox);
        }

        return div;
    }

    private _createCheckbox(id: string, params: ICustomizeItemModifierMultiElement, halfId: HalfModifierSelection): HTMLElement {
        const data = params.selectedData.get(halfId) ?? params.selectedData.get(HalfModifierSelection.WHOLE);
        const isSelected = data?.isSelected ?? false;

        if (data?.isDisabled)
            console.log(params)

        const radioId = params.modifierDisplayGroup?.IsHalfable ? `${params.radioId}_${halfId}` : params.radioId;
        const side = HalfModifierSelection.RIGHT ? "ModifierRightSide" : "ModifierLeftSide";
        const html = `
            <label class="custom-radio">
                <input type="${params.isMultiSelect ? "checkbox" : "radio"}" id="${id}" name="${radioId}" aria-label="${this._localizationService.names(side).replace("{{modifier_name}}", params.name)}" ${isSelected ? `checked="checked"` : ""} ${data?.isDisabled ? "disabled" : ""}>
                <span class="checkmark"></span>
            </label>`;

        const label = Util.createHtmlElementFromTemplate(html);

        // Attach click handler based on modifier type
        const checkbox = label.querySelector(`#${id}`) as HTMLInputElement;
        if (checkbox)
            checkbox.onclick = (event: Event) => {
                if (!params.isSize)
                    event.preventDefault();
                event.stopPropagation();
            };

        label.onclick = () => {
            if (data?.isDisabled)
                return;

            if (params.modifierDisplayGroup === null)
                this._emitSizeClick(params.modifierId);
            else
                this._emitModifierClick(
                    id,
                    params.modifierDisplayGroup.ModifierDisplayGroupId,
                    params.modifierId,
                    halfId,
                    params.isMultiSelect
                );
        };

        return label;
    }

    /**
     * Adds additional UI elements such as premodifiers and half-selection bars to the given modifier option.
     * 
     * @param {HTMLElement} optionDiv - The DOM element to append helper components to.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The display group containing the modifier.
     * @param {IItemModifierDisplayGroupModifier} modifier - The specific modifier data.
     * @param {HalfModifierSelection} halfId - The half modifier selection state (e.g., whole, left, right).
     * @param {IDataItem} menuItem - The menu item to which this modifier belongs.
     */
    protected _addModifierAdditionalElements(
        optionDiv: HTMLElement,
        params: ICustomizeItemModifierMultiElement
    ): void {
        if (!params.modifierDisplayGroup?.IsWeightChangeable || (!params.leftModifier && !params.rightModifier))
            return;

        const isMenuItemHalfable = [...params.selectedData.values()].every(v => v.menuItem.IsHalfable);

        if (!this._item.isHalfMode || !params.modifierDisplayGroup.IsHalfable || !isMenuItemHalfable)
            return;

        const defaultPreModifierId = this._data.getProperty("DefaultPreModifierId");

        const parentDiv = document.createElement("div");
        parentDiv.classList.add("customize-item-premodifier-selection-parent");

        const modifierId = params.leftModifier?.ModifierId ?? params.rightModifier?.ModifierId!;

        const left = params.selectedData.get(HalfModifierSelection.LEFT) ?? params.selectedData.get(HalfModifierSelection.WHOLE);
        const preModParams: ICustomizeItemPremodifierBaseParams = {
            modifierDisplayGroup: params.modifierDisplayGroup,
            modifierId: modifierId,
            halfId: HalfModifierSelection.LEFT,
            isSelected: left?.isSelected ?? false,
            preModifierId: left?.preModifierId ?? defaultPreModifierId,
            preModifierExceptions: params.leftModifier?.PreModifierException ?? [],
            modifierName: params.name
        };

        const premodifierElementLeft = new CustomizeItemPreModifierMultiItem(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            preModParams
        );

        if (premodifierElementLeft.element)
            parentDiv.appendChild(premodifierElementLeft.element);

        const right = params.selectedData.get(HalfModifierSelection.RIGHT) ?? params.selectedData.get(HalfModifierSelection.WHOLE);
        preModParams.halfId = HalfModifierSelection.RIGHT;
        preModParams.isSelected = right?.isSelected ?? false;
        preModParams.preModifierId = right?.preModifierId ?? defaultPreModifierId;
        preModParams.preModifierExceptions = params.rightModifier?.PreModifierException ?? [];

        const premodifierElementRight = new CustomizeItemPreModifierMultiItem(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            preModParams
        );

        if (premodifierElementRight.element)
            parentDiv.appendChild(premodifierElementRight.element);

        optionDiv.appendChild(parentDiv);
    }
}